import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { applyHoverIn, applyHoverOut, chromeButtonBase, colors, dimensions, fonts } from '../terminal-tokens';
import { TabItem } from './TabItem';
import { TabOverflowMenu } from './TabOverflowMenu';
const tabBarStyle = {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: colors.bgChrome,
    borderBottom: `1px solid ${colors.border}`,
    height: dimensions.tabBarHeight,
    flexShrink: 0,
    position: 'relative',
    userSelect: 'none',
};
const scrollBtnStyle = {
    ...chromeButtonBase,
    width: dimensions.scrollBtnWidth,
    fontSize: 16,
};
const tabsContainerStyle = {
    display: 'flex',
    alignItems: 'stretch',
    overflowX: 'auto',
    flex: 1,
    scrollBehavior: 'smooth',
    /* Hide scrollbar */
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
};
const newTabBtnStyle = {
    ...chromeButtonBase,
    width: dimensions.newTabBtnWidth,
    borderLeft: `1px solid ${colors.border}`,
    fontSize: 18,
    fontWeight: 300,
    transition: 'color 0.15s, background-color 0.15s',
};
const overflowBtnStyle = {
    ...chromeButtonBase,
    width: dimensions.overflowBtnWidth,
    borderLeft: `1px solid ${colors.border}`,
    fontSize: 16,
    fontFamily: fonts.ui,
    position: 'relative',
};
/**
 * Terminal tab bar component
 * Manages tab display, scrolling, and overflow
 */
export const TabBar = ({ sessions, activeSessionId, onTabActivate, onTabClose, onTabRename, onNewTab, maxTabs, }) => {
    const tabsContainerRef = useRef(null);
    const hadTabFocusRef = useRef(false);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);
    const [showOverflowMenu, setShowOverflowMenu] = useState(false);
    const [focusedSessionId, setFocusedSessionId] = useState(activeSessionId);
    // Check if scrolling is needed
    useEffect(() => {
        const checkScroll = () => {
            const container = tabsContainerRef.current;
            if (!container)
                return;
            const { scrollLeft, scrollWidth, clientWidth } = container;
            setShowLeftScroll(scrollLeft > 0);
            setShowRightScroll(scrollLeft + clientWidth < scrollWidth);
        };
        checkScroll();
        const container = tabsContainerRef.current;
        container?.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
            container?.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [sessions]);
    // Scroll to active tab when it changes
    useEffect(() => {
        if (activeSessionId && tabsContainerRef.current) {
            const activeTab = tabsContainerRef.current.querySelector(`[data-session-id="${activeSessionId}"]`);
            if (activeTab && activeTab.scrollIntoView) {
                activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [activeSessionId]);
    const focusTab = useCallback((sessionId) => {
        setFocusedSessionId(sessionId);
        requestAnimationFrame(() => {
            const tab = tabsContainerRef.current?.querySelector(`[data-session-id="${sessionId}"]`);
            tab?.focus();
        });
    }, []);
    const sortedSessions = [...sessions].sort((a, b) => a.order - b.order);
    const rovingSessionId = sortedSessions.some((session) => session.id === focusedSessionId)
        ? focusedSessionId
        : activeSessionId && sortedSessions.some((session) => session.id === activeSessionId)
            ? activeSessionId
            : sortedSessions[0]?.id ?? null;
    useEffect(() => {
        if (!activeSessionId)
            return;
        const activeTab = tabsContainerRef.current?.querySelector(`[data-session-id="${activeSessionId}"]`);
        const focusWithinTabList = hadTabFocusRef.current
            || (tabsContainerRef.current?.contains(document.activeElement) ?? false);
        setFocusedSessionId(activeSessionId);
        if (focusWithinTabList) {
            activeTab?.focus();
        }
    }, [activeSessionId]);
    useEffect(() => {
        if (rovingSessionId === focusedSessionId)
            return;
        setFocusedSessionId(rovingSessionId);
    }, [focusedSessionId, rovingSessionId]);
    const handleTabKeyDown = useCallback((e, sessionId) => {
        const currentIndex = sortedSessions.findIndex((session) => session.id === sessionId);
        if (currentIndex === -1 || sortedSessions.length === 0)
            return;
        let nextIndex = null;
        if (e.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % sortedSessions.length;
        }
        else if (e.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + sortedSessions.length) % sortedSessions.length;
        }
        else if (e.key === 'Home') {
            nextIndex = 0;
        }
        else if (e.key === 'End') {
            nextIndex = sortedSessions.length - 1;
        }
        if (nextIndex !== null) {
            e.preventDefault();
            const nextSession = sortedSessions[nextIndex];
            if (nextSession)
                focusTab(nextSession.id);
        }
    }, [focusTab, sortedSessions]);
    const handleScroll = (direction) => {
        const container = tabsContainerRef.current;
        if (!container)
            return;
        const scrollAmount = 200;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };
    const canCreateNewTab = sessions.length < maxTabs;
    return (_jsxs("div", { style: tabBarStyle, role: "tablist", children: [showLeftScroll && (_jsx("button", { style: scrollBtnStyle, onClick: () => handleScroll('left'), "aria-label": "Scroll tabs left", children: "\u2039" })), _jsx("div", { style: tabsContainerStyle, ref: tabsContainerRef, onFocusCapture: (event) => {
                    if (event.target.getAttribute('role') === 'tab') {
                        hadTabFocusRef.current = true;
                    }
                }, onBlurCapture: () => {
                    requestAnimationFrame(() => {
                        hadTabFocusRef.current = tabsContainerRef.current?.contains(document.activeElement) ?? false;
                    });
                }, children: sortedSessions.map((session) => (_jsx(TabItem, { session: session, isActive: session.id === activeSessionId, tabIndex: session.id === rovingSessionId ? 0 : -1, onActivate: onTabActivate, onClose: onTabClose, onRename: onTabRename, onKeyDown: handleTabKeyDown }, session.id))) }), showRightScroll && (_jsx("button", { style: scrollBtnStyle, onClick: () => handleScroll('right'), "aria-label": "Scroll tabs right", children: "\u203A" })), canCreateNewTab && (_jsx("button", { style: newTabBtnStyle, onClick: onNewTab, "aria-label": "Create new terminal", title: "New Terminal (Ctrl+Shift+T)", onMouseEnter: (e) => applyHoverIn(e.currentTarget), onMouseLeave: (e) => applyHoverOut(e.currentTarget), children: "+" })), sessions.length > 5 && (_jsxs("div", { style: { position: 'relative', flexShrink: 0 }, children: [_jsx("button", { style: overflowBtnStyle, onClick: () => setShowOverflowMenu(!showOverflowMenu), "aria-label": "Show all terminals", "aria-expanded": showOverflowMenu, "aria-haspopup": "menu", title: "All Terminals", children: "\u22EE" }), showOverflowMenu && (_jsx(TabOverflowMenu, { sessions: sortedSessions, activeSessionId: activeSessionId, onSelect: (id) => {
                            onTabActivate(id);
                            setShowOverflowMenu(false);
                        }, onClose: () => setShowOverflowMenu(false) }))] }))] }));
};
//# sourceMappingURL=TabBar.js.map